const integer = (value: unknown, fallback = 0) => {
  const number = Number(value);
  return Number.isInteger(number) ? number : fallback;
};

export type PerkRankAction = {
  enabled: boolean;
  nextRank: number;
  mode: "invalid" | "remove" | "decrease" | "increase";
  cost: number;
};

export function resolvePerkRankAction({
  currentRank,
  targetRank,
  maxRanks,
  freeSlots,
  canIncrease = true,
}: {
  currentRank: unknown;
  targetRank: unknown;
  maxRanks: unknown;
  freeSlots: unknown;
  canIncrease?: boolean;
}): PerkRankAction {
  const maximum = Math.max(1, integer(maxRanks, 1));
  const current = Math.min(maximum, Math.max(0, integer(currentRank)));
  const target = integer(targetRank, -1);
  const available = Math.max(0, integer(freeSlots));
  if (target < 1 || target > maximum)
    return { enabled: false, nextRank: current, mode: "invalid", cost: 0 };
  if (target < current)
    return { enabled: true, nextRank: target, mode: "decrease", cost: 0 };
  if (target === current) {
    const nextRank = Math.max(0, current - 1);
    return {
      enabled: current > 0,
      nextRank,
      mode: nextRank ? "decrease" : "remove",
      cost: 0,
    };
  }
  const cost = target - current;
  return {
    enabled: canIncrease && cost <= available,
    nextRank: target,
    mode: "increase",
    cost,
  };
}

export function perkRankPreview({
  currentRank,
  previewRank,
  maxRanks,
}: {
  currentRank: unknown;
  previewRank: unknown;
  maxRanks: unknown;
}) {
  const maximum = Math.max(1, integer(maxRanks, 1));
  const current = Math.min(maximum, Math.max(0, integer(currentRank)));
  const hasPreview = previewRank !== null && previewRank !== undefined;
  const preview = hasPreview
    ? Math.min(maximum, Math.max(0, integer(previewRank)))
    : current;
  return Array.from({ length: maximum }, (_, index) => {
    const rank = index + 1;
    const filled = rank <= current;
    const removal =
      hasPreview && preview < current && rank > preview && rank <= current;
    return {
      rank,
      filled,
      displayFilled: filled && !removal,
      addition:
        hasPreview && preview > current && rank > current && rank <= preview,
      removal,
    };
  });
}
